import { useRoomContext } from "@livekit/components-react"
import { RoomEvent } from "livekit-client"
import React, { useEffect, useState } from "react"
import { Pressable, View } from "react-native"
import TextM from "../../components/text/TextM"
import { theme } from "../../theme/theme"

/**
 * Student-only tap-to-resume overlay.
 *
 * Browser autoplay policy can leave the <audio> element paused after a
 * background/reconnect with no user gesture (`room.canPlaybackAudio === false`): the
 * student sees the waveforms move but hears nothing. We surface a tappable overlay that
 * calls `room.startAudio()` from inside the gesture to unlock playback. Renders nothing
 * while playback is allowed, so it is inert during healthy sessions.
 */
export function StartAudioOverlay() {
    const room = useRoomContext()
    const [needsGesture, setNeedsGesture] = useState(false)

    useEffect(() => {
        if (!room) return
        const sync = () => setNeedsGesture(!room.canPlaybackAudio)
        sync()
        room.on(RoomEvent.AudioPlaybackStatusChanged, sync)
        return () => {
            room.off(RoomEvent.AudioPlaybackStatusChanged, sync)
        }
    }, [room])

    if (!needsGesture) return null

    const resumeAudio = async () => {
        try {
            await room.startAudio()
        } catch (e) {
            console.log("startAudio failed", e)
        } finally {
            setNeedsGesture(!room.canPlaybackAudio)
        }
    }

    return (
        <Pressable
            onPress={resumeAudio}
            style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0,0,0,0.6)",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 99999,
            }}
        >
            <View
                style={{
                    backgroundColor: theme.colors.white,
                    paddingVertical: 18,
                    paddingHorizontal: 24,
                    borderRadius: 16,
                    maxWidth: 320,
                    alignItems: "center",
                }}
            >
                <TextM
                    wrap
                    center
                    fontWeight="Bold"
                    fontSize={16}
                    style={{ marginBottom: 6 }}
                >
                    Touche l'écran pour réactiver le son 🔊
                </TextM>
                <TextM wrap center color={theme.colors.grey}>
                    Ton navigateur a mis l'audio en pause. Un simple appui le relance.
                </TextM>
            </View>
        </Pressable>
    )
}
